// ArticleCreateGutenberg.jsx - COMPONENTE DE CREACIÓN
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ArticleGutenberg.css';

// Reutilizar los mismos componentes del ArticleEditGutenberg
const ImageUploader = ({ onImageUploaded }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [fileInfo, setFileInfo] = useState(null);

  const validateFile = (file) => {
    const ALLOWED_TYPES = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
      'image/tiff',
      'image/svg+xml',
    ];

    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(
        'Tipo de archivo no permitido. Usa formatos de imagen comunes.'
      );
    }

    return true;
  };

  const getImageDimensions = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        URL.revokeObjectURL(url);
        resolve({ width, height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({ width: 0, height: 0 });
      };

      img.src = url;
    });
  };

  const calculateAspectRatio = (width, height) => {
    if (width === 0 || height === 0) return '16/9';

    const ratio = width / height;

    if (Math.abs(ratio - 1) < 0.1) return '1/1';
    if (Math.abs(ratio - 4 / 3) < 0.1) return '4/3';
    if (Math.abs(ratio - 3 / 2) < 0.1) return '3/2';
    if (Math.abs(ratio - 16 / 9) < 0.1) return '16/9';

    const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
    const divisor = gcd(width, height);
    const simplifiedWidth = width / divisor;
    const simplifiedHeight = height / divisor;

    if (simplifiedWidth <= 20 && simplifiedHeight <= 20) {
      return `${simplifiedWidth}/${simplifiedHeight}`;
    }

    return '16/9';
  };

  const handleFileSelect = async (file) => {
    if (!file) return;

    try {
      validateFile(file);
      setError(null);

      setFileInfo({
        name: file.name,
        size: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
        type: file.type,
      });
    } catch (validationError) {
      setError(validationError.message);
      setFileInfo(null);
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      const fileToUpload = file;

      const formData = new FormData();
      formData.append('file', fileToUpload);
      formData.append('alt_text', '');
      formData.append('description', '');

      const dimensions = await getImageDimensions(file);
      const aspectRatio = calculateAspectRatio(
        dimensions.width,
        dimensions.height
      );

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100;
          setProgress(Math.min(percentComplete, 90));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          setProgress(100);
          try {
            const imageData = JSON.parse(xhr.responseText);

            const enrichedImageData = {
              ...imageData,
              dimensions: {
                width: dimensions.width,
                height: dimensions.height,
              },
              aspectRatio: aspectRatio,
              linkUrl: imageData.aws_path || imageData.url,
            };

            setTimeout(() => {
              onImageUploaded(enrichedImageData);
              setUploading(false);
              setProgress(0);
              setError(null);
              setFileInfo(null);
            }, 500);
          } catch (parseError) {
            setError('Error al procesar la respuesta del servidor');
            setUploading(false);
          }
        } else {
          handleUploadError(xhr.status, xhr.statusText);
        }
      });

      xhr.addEventListener('error', () => {
        handleUploadError(0, 'Error de conexión');
      });

      xhr.addEventListener('timeout', () => {
        handleUploadError(0, 'Timeout - La subida tardó demasiado');
      });

      xhr.timeout = 300000;
      xhr.open('POST', `${process.env.REACT_APP_API_BASE_URL}/images/upload`);
      xhr.send(formData);
    } catch (error) {
      console.error('Error en upload:', error);
      setUploading(false);
      setProgress(0);
      setError(error.message || 'Error al procesar la imagen');
    }
  };

  const handleUploadError = (status, message) => {
    let errorMessage = 'Error al subir la imagen';

    switch (status) {
      case 413:
        errorMessage =
          'ERROR 413: El servidor rechazó la imagen por ser muy grande. Esto es un límite del servidor, no del frontend. Contacta al administrador para que aumente el límite en el servidor.';
        break;
      case 0:
        errorMessage = 'Error de conexión. Verifica tu internet.';
        break;
      case 500:
        errorMessage = 'Error interno del servidor. Intenta más tarde.';
        break;
      case 404:
        errorMessage = 'Servicio no encontrado. Verifica la URL.';
        break;
      default:
        errorMessage = `Error ${status}: ${message}`;
    }

    setError(errorMessage);
    setUploading(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  return (
    <div
      className={`image-uploader ${dragOver ? 'drag-over' : ''} ${
        uploading ? 'uploading' : ''
      } ${error ? 'error' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {error && (
        <div className="upload-error">
          <span className="error-icon">❌</span>
          <p className="error-message">{error}</p>
          {error.includes('413') && (
            <div className="error-suggestions">
              <p>
                <strong>
                  El problema está en el servidor, no en esta aplicación.
                </strong>
              </p>
              <p>El administrador necesita configurar:</p>
              <ul>
                <li>
                  Nginx: <code>client_max_body_size 100M;</code>
                </li>
                <li>
                  Apache: <code>LimitRequestBody 104857600</code>
                </li>
                <li>
                  PHP: <code>upload_max_filesize = 100M</code>
                </li>
                <li>
                  PHP: <code>post_max_size = 100M</code>
                </li>
              </ul>
            </div>
          )}
          <button
            onClick={() => {
              setError(null);
              setFileInfo(null);
            }}
            className="btn-retry"
          >
            Intentar con otra imagen
          </button>
        </div>
      )}

      {!error && fileInfo && !uploading && (
        <div className="file-info">
          <p>
            <strong>Archivo seleccionado:</strong> {fileInfo.name}
          </p>
          <p>
            <strong>Tamaño:</strong> {fileInfo.size}
          </p>
          <p>
            <strong>Tipo:</strong> {fileInfo.type}
          </p>
          <p className="file-ready">✓ Listo para subir</p>
        </div>
      )}

      {uploading ? (
        <div className="upload-progress">
          <div className="progress-circle">
            <svg width="60" height="60" viewBox="0 0 60 60">
              <circle
                cx="30"
                cy="30"
                r="27"
                stroke="#e0e0e0"
                strokeWidth="3"
                fill="none"
              />
              <circle
                cx="30"
                cy="30"
                r="27"
                stroke="#007cba"
                strokeWidth="3"
                fill="none"
                strokeDasharray={169.64}
                strokeDashoffset={169.64 - (progress / 100) * 169.64}
                transform="rotate(-90 30 30)"
              />
            </svg>
            <span className="progress-text">{Math.round(progress)}%</span>
          </div>
          <p>Subiendo imagen...</p>
          {fileInfo && (
            <p className="file-info-uploading">
              {fileInfo.name} ({fileInfo.size})
            </p>
          )}
          {progress > 50 && progress < 90 && (
            <p className="upload-tip">
              Subiendo archivo grande, por favor espera...
            </p>
          )}
        </div>
      ) : (
        !error && (
          <>
            <div className="upload-placeholder">
              <span className="upload-icon">🖼️</span>
              <p>Arrastra una imagen aquí o haz clic para seleccionar</p>
              <p className="file-requirements">
                • Cualquier tamaño aceptado • Formatos: JPEG, PNG, GIF, WebP,
                BMP, TIFF, SVG
              </p>
            </div>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => handleFileSelect(e.target.files[0])}
              className="file-input"
            />
          </>
        )
      )}
    </div>
  );
};

// Componente de imagen editable
const ImageBlock = ({ block, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [size, setSize] = useState(block.size || 'large');
  const [alignment, setAlignment] = useState(block.alignment || 'center');
  const [altText, setAltText] = useState(block.altText || '');
  const [caption, setCaption] = useState(block.caption || '');
  const [linkUrl, setLinkUrl] = useState(block.linkUrl || block.aws_path || '');
  const [aspectRatio, setAspectRatio] = useState(
    block.wpAttributes?.aspectRatio || '16/9'
  );
  const [scale, setScale] = useState(block.wpAttributes?.scale || 'cover');

  const sizes = {
    thumbnail: 'Miniatura (150px)',
    medium: 'Mediano (300px)',
    large: 'Grande (1024px)',
    full: 'Completa (Original)',
  };

  const scales = {
    cover: 'Cover (Cubrir)',
    contain: 'Contain (Contener)',
    fill: 'Fill (Llenar)',
    none: 'None (Ninguno)',
  };

  const aspectRatios = {
    '1/1': '1:1 (Cuadrado)',
    '4/3': '4:3 (Estándar)',
    '16/9': '16:9 (Widescreen)',
    '3/2': '3:2 (Fotografía)',
    custom: 'Personalizado',
  };

  const handleSave = () => {
    onUpdate(block.id, {
      ...block,
      size,
      alignment,
      altText,
      caption,
      linkUrl,
      wpAttributes: {
        ...block.wpAttributes,
        aspectRatio,
        scale,
        sizeSlug: size,
        linkDestination: linkUrl ? 'media' : 'none',
      },
    });
    setEditing(false);
  };

  return (
    <div className={`image-block ${alignment} ${editing ? 'editing' : ''}`}>
      <div className="block-toolbar">
        <span className="block-type">🖼️ Imagen</span>
        <div className="toolbar-actions">
          <button onClick={() => setEditing(!editing)} className="btn-edit">
            {editing ? '👁️ Ver' : '⚙️ Editar'}
          </button>
          <button onClick={onDelete} className="btn-delete">
            🗑️
          </button>
        </div>
      </div>

      <div className="image-content">
        {editing ? (
          <div className="image-editor">
            <div className="image-preview">
              <img
                src={block.url}
                alt={altText}
                className={size}
                style={{
                  aspectRatio: aspectRatio,
                  objectFit: scale,
                }}
              />
            </div>

            <div className="image-controls">
              <div className="control-group">
                <label>Tamaño:</label>
                <select value={size} onChange={(e) => setSize(e.target.value)}>
                  {Object.entries(sizes).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label>Alineación:</label>
                <div className="alignment-buttons">
                  {['left', 'center', 'right'].map((align) => (
                    <button
                      key={align}
                      className={`align-btn ${
                        alignment === align ? 'active' : ''
                      }`}
                      onClick={() => setAlignment(align)}
                    >
                      {align === 'left' && '⬅️'}
                      {align === 'center' && '🔘'}
                      {align === 'right' && '➡️'}
                    </button>
                  ))}
                </div>
              </div>

              <div className="control-group">
                <label>Relación de aspecto:</label>
                <select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value)}
                >
                  {Object.entries(aspectRatios).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label>Escala de imagen:</label>
                <select
                  value={scale}
                  onChange={(e) => setScale(e.target.value)}
                >
                  {Object.entries(scales).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="control-group">
                <label>Enlace de imagen:</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                />
              </div>

              <div className="control-group">
                <label>Texto alternativo:</label>
                <input
                  type="text"
                  value={altText}
                  onChange={(e) => setAltText(e.target.value)}
                  placeholder="Describe la imagen..."
                />
              </div>

              <div className="control-group">
                <label>Pie de foto:</label>
                <input
                  type="text"
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  placeholder="Pie de foto..."
                />
              </div>

              <div className="editor-actions">
                <button onClick={handleSave} className="btn-save">
                  💾 Aplicar
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="btn-cancel"
                >
                  ❌ Cancelar
                </button>
              </div>
            </div>
          </div>
        ) : (
          <figure className={`image-display ${alignment}`}>
            {block.linkUrl ? (
              <a href={block.linkUrl}>
                <img
                  src={block.url}
                  alt={altText}
                  className={size}
                  style={{
                    aspectRatio: block.wpAttributes?.aspectRatio || '16/9',
                    objectFit: block.wpAttributes?.scale || 'cover',
                  }}
                />
              </a>
            ) : (
              <img
                src={block.url}
                alt={altText}
                className={size}
                style={{
                  aspectRatio: block.wpAttributes?.aspectRatio || '16/9',
                  objectFit: block.wpAttributes?.scale || 'cover',
                }}
              />
            )}
            {caption && (
              <figcaption dangerouslySetInnerHTML={{ __html: caption }} />
            )}
          </figure>
        )}
      </div>
    </div>
  );
};

// Componente de heading mejorado
const HeadingBlock = ({ block, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(block.content || '');
  const [level, setLevel] = useState(block.level || 2);

  const handleSave = () => {
    onUpdate(block.id, { ...block, content, level });
    setEditing(false);
  };

  const renderHeading = () => {
    const HeadingTag = `h${level}`;
    return (
      <HeadingTag
        className="editable-heading"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  return (
    <div className="heading-block">
      <div className="block-toolbar">
        <span className="block-type">🏷️ Título H{level}</span>
        <div className="toolbar-actions">
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn-edit">
              ✏️
            </button>
          )}
          <button onClick={onDelete} className="btn-delete">
            🗑️
          </button>
        </div>
      </div>

      {editing ? (
        <div className="heading-editor">
          <div className="level-selector">
            <label>Nivel:</label>
            <select
              value={level}
              onChange={(e) => setLevel(parseInt(e.target.value))}
            >
              {[1, 2, 3, 4, 5, 6].map((lvl) => (
                <option key={lvl} value={lvl}>
                  H{lvl}
                </option>
              ))}
            </select>
          </div>
          <div
            className="heading-content-editable"
            contentEditable
            dangerouslySetInnerHTML={{ __html: content }}
            onBlur={(e) => setContent(e.currentTarget.innerHTML)}
            style={{
              border: '1px solid #ccc',
              padding: '8px',
              minHeight: '60px',
              borderRadius: '4px',
              fontSize: '1.5em',
              fontWeight: 'bold',
              whiteSpace: 'pre-wrap',
            }}
          />
          <div className="editor-actions">
            <button onClick={handleSave} className="btn-save">
              💾 Guardar
            </button>
            <button onClick={() => setEditing(false)} className="btn-cancel">
              ❌ Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="heading-content">{renderHeading()}</div>
      )}
    </div>
  );
};

// Componente para gestionar Categorías y Tags
const TaxonomyManager = ({
  selectedCategories,
  setSelectedCategories,
  selectedTags,
  setSelectedTags,
  selectedAuthor,
  setSelectedAuthor,
  allCategories,
  allTags,
  allAuthors,
}) => {
  const [newTagName, setNewTagName] = useState('');

  // Manejar selección de categorías
  const handleCategoryToggle = (categoryId) => {
    const newSelectedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newSelectedCategories);
  };

  const handleTagToggle = (tagId) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter((id) => id !== tagId)
      : [...selectedTags, tagId];

    setSelectedTags(newSelectedTags);
  };

  const handleAuthorChange = async (authorId) => {
    const newAuthorId = authorId === '' ? null : parseInt(authorId);
    setSelectedAuthor(newAuthorId);
  };

  // Crear nuevo tag
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/tags/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newTagName.trim() }),
        }
      );

      if (response.ok) {
        const newTag = await response.json();
        const newSelectedTags = [...selectedTags, newTag.id];
        setSelectedTags(newSelectedTags);
        setNewTagName('');

        // Recargar la página para actualizar la lista de tags
        window.location.reload();
      }
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };

  // Función recursiva para mostrar categorías
  const renderCategoryTree = (categories, level = 0) => {
    return categories.map((category) => (
      <div key={category.id} style={{ marginLeft: level > 0 ? '20px' : '0' }}>
        <label className="category-item">
          <input
            type="checkbox"
            checked={selectedCategories.includes(category.id)}
            onChange={() => handleCategoryToggle(category.id)}
          />
          <span className="category-name">{category.name}</span>
          {category.article_count > 0 && (
            <span className="article-count">({category.article_count})</span>
          )}
        </label>
        {category.children && category.children.length > 0 && (
          <div className="category-children">
            {renderCategoryTree(category.children, level + 1)}
          </div>
        )}
      </div>
    ));
  };

  // Función helper para buscar categorías por ID
  const findCategoryById = (categories, id) => {
    for (const category of categories) {
      if (category.id === id) return category;
      if (category.children) {
        const found = findCategoryById(category.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  return (
    <div className="taxonomy-manager">
      <div className="taxonomy-section">
        <h4>Autor</h4>
        <div className="author-selector">
          <select
            value={selectedAuthor || ''}
            onChange={(e) => handleAuthorChange(e.target.value)}
          >
            <option value="">Seleccionar autor...</option>
            {allAuthors.map((author) => (
              <option key={author.id} value={author.id}>
                {author.display_name}
              </option>
            ))}
          </select>
        </div>
        {selectedAuthor && (
          <div className="selected-author mt-2">
            <strong>Autor seleccionado:</strong>{' '}
            {allAuthors.find((a) => a.id === selectedAuthor)?.display_name ||
              'N/A'}
          </div>
        )}
      </div>
      <div className="taxonomy-section">
        <h4>Categorías</h4>
        <div className="categories-tree">
          {allCategories.length > 0 ? (
            renderCategoryTree(allCategories)
          ) : (
            <p>Cargando categorías...</p>
          )}
        </div>
      </div>

      <div className="taxonomy-section">
        <h4>Etiquetas</h4>
        <div className="tags-container">
          <div className="tags-list">
            {allTags.map((tag) => (
              <label key={tag.id} className="tag-item">
                <input
                  type="checkbox"
                  checked={selectedTags.includes(tag.id)}
                  onChange={() => handleTagToggle(tag.id)}
                />
                <span className="tag-name">{tag.name}</span>
              </label>
            ))}
          </div>

          <div className="new-tag-creator">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder="Nueva etiqueta..."
              onKeyPress={(e) => e.key === 'Enter' && handleCreateTag()}
            />
            <button onClick={handleCreateTag} className="btn-add-tag">
              + Crear
            </button>
          </div>
        </div>
      </div>

      <div className="selected-taxonomies">
        <h4>Seleccionados:</h4>
        <div className="selected-items">
          <div className="selected-categories">
            <strong>Categorías:</strong>{' '}
            {selectedCategories.length > 0
              ? selectedCategories
                  .map((id) => {
                    const category = findCategoryById(allCategories, id);
                    return category?.name;
                  })
                  .filter(Boolean)
                  .join(', ')
              : 'Ninguna'}
          </div>
          <div className="selected-tags">
            <strong>Etiquetas:</strong>{' '}
            {selectedTags.length > 0
              ? selectedTags
                  .map((id) => {
                    const tag = allTags.find((t) => t.id === id);
                    return tag?.name;
                  })
                  .filter(Boolean)
                  .join(', ')
              : 'Ninguna'}
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente de Notificación
const Notification = ({
  type,
  title,
  message,
  onClose,
  onViewArticle,
  autoClose = true,
}) => {
  useEffect(() => {
    if (autoClose) {
      const timer = setTimeout(() => {
        onClose();
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [autoClose, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return '🎉';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      default:
        return 'ℹ️';
    }
  };

  return (
    <div className={`notification ${type}`}>
      <button className="notification-close" onClick={onClose}>
        ×
      </button>

      <div className="notification-header">
        <span className="notification-icon">{getIcon()}</span>
        <h3 className="notification-title">{title}</h3>
      </div>

      <p className="notification-message">{message}</p>

      <div className="notification-actions">
        {onViewArticle && (
          <button className="notification-btn primary" onClick={onViewArticle}>
            Ver artículo
          </button>
        )}
        <button className="notification-btn secondary" onClick={onClose}>
          Cerrar
        </button>
      </div>
    </div>
  );
};

// Componente contenedor de notificaciones
const NotificationContainer = ({ notifications, removeNotification }) => {
  return (
    <div className="notification-container">
      {notifications.map((notification) => (
        <Notification
          key={notification.id}
          type={notification.type}
          title={notification.title}
          message={notification.message}
          onClose={() => removeNotification(notification.id)}
          onViewArticle={notification.onViewArticle}
        />
      ))}
    </div>
  );
};

// Componente EditableBlock (para párrafos y listas)
const EditableBlock = ({ block, onUpdate, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(block.content || '');
  const [listItems, setListItems] = useState(block.items || ['']);

  const handleSave = () => {
    if (block.type === 'list') {
      onUpdate(block.id, {
        ...block,
        items: listItems.filter((item) => item.trim() !== ''),
      });
    } else {
      onUpdate(block.id, { ...block, content });
    }
    setEditing(false);
  };

  const addListItem = () => {
    setListItems([...listItems, '']);
  };

  const updateListItem = (index, value) => {
    const newItems = [...listItems];
    newItems[index] = value;
    setListItems(newItems);
  };

  const removeListItem = (index) => {
    setListItems(listItems.filter((_, i) => i !== index));
  };

  const renderContent = () => {
    switch (block.type) {
      case 'paragraph':
        return (
          <div
            className="editable-p"
            dangerouslySetInnerHTML={{ __html: content }}
          />
        );
      case 'list':
        return (
          <div className="editable-list">
            {block.ordered ? (
              <ol>
                {block.items?.map((item, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ol>
            ) : (
              <ul>
                {block.items?.map((item, index) => (
                  <li key={index} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ul>
            )}
          </div>
        );
      default:
        return <div dangerouslySetInnerHTML={{ __html: content }} />;
    }
  };

  return (
    <div className={`editable-block ${block.type}`}>
      <div className="block-toolbar">
        <span className="block-type">
          {block.type === 'paragraph' ? '📝 Párrafo' : '📋 Lista'}
        </span>
        <div className="toolbar-actions">
          {!editing && (
            <button onClick={() => setEditing(true)} className="btn-edit">
              ✏️
            </button>
          )}
          <button onClick={onDelete} className="btn-delete">
            🗑️
          </button>
        </div>
      </div>

      {editing ? (
        <div className="block-editor">
          {block.type === 'list' ? (
            <div className="list-editor">
              {listItems.map((item, index) => (
                <div key={index} className="list-item-editor">
                  <textarea
                    value={item}
                    onChange={(e) => updateListItem(index, e.target.value)}
                    placeholder={`Elemento de lista ${index + 1}`}
                    rows="2"
                    className="list-item-textarea"
                  />
                  <button
                    onClick={() => removeListItem(index)}
                    className="btn-remove-item"
                    disabled={listItems.length === 1}
                  >
                    ❌
                  </button>
                </div>
              ))}
              <button onClick={addListItem} className="btn-add-item">
                + Añadir elemento
              </button>
            </div>
          ) : (
            <div
              className="content-editable"
              contentEditable
              dangerouslySetInnerHTML={{ __html: content }}
              onBlur={(e) => setContent(e.currentTarget.innerHTML)}
              style={{
                border: '1px solid #ccc',
                padding: '8px',
                minHeight: '100px',
                borderRadius: '4px',
                whiteSpace: 'pre-wrap',
              }}
            />
          )}
          <div className="editor-actions">
            <button onClick={handleSave} className="btn-save">
              💾 Guardar
            </button>
            <button onClick={() => setEditing(false)} className="btn-cancel">
              ❌ Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="block-content">{renderContent()}</div>
      )}
    </div>
  );
};

// Componente principal de creación
export default function ArticleCreateGutenberg() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [blocks, setBlocks] = useState([]);
  const [title, setTitle] = useState('');
  const [featuredImage, setFeaturedImage] = useState('');
  const [showBlockInserter, setShowBlockInserter] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [allAuthors, setAllAuthors] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedAuthors, setSelectedAuthors] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [lastActiveBlockId, setLastActiveBlockId] = useState(null);
  const blockRefs = useRef({});

  // Función para agregar notificaciones
  const addNotification = (notification) => {
    const id = Date.now();
    const newNotification = {
      id,
      ...notification,
    };

    setNotifications((prev) => [...prev, newNotification]);
    return id;
  };

  // Función para remover notificaciones
  const removeNotification = (id) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id)
    );
  };

  // Efecto de confeti
  const createConfettiEffect = () => {
    const confettiCount = 200;
    const confettiContainer = document.createElement('div');
    confettiContainer.style.position = 'fixed';
    confettiContainer.style.top = '0';
    confettiContainer.style.left = '0';
    confettiContainer.style.width = '100%';
    confettiContainer.style.height = '100%';
    confettiContainer.style.pointerEvents = 'none';
    confettiContainer.style.zIndex = '9999';
    document.body.appendChild(confettiContainer);

    const colors = [
      '#ff0000',
      '#00ff00',
      '#0000ff',
      '#ffff00',
      '#ff00ff',
      '#00ffff',
    ];

    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.style.position = 'absolute';
      confetti.style.width = '10px';
      confetti.style.height = '10px';
      confetti.style.backgroundColor =
        colors[Math.floor(Math.random() * colors.length)];
      confetti.style.borderRadius = '50%';
      confetti.style.top = '0';
      confetti.style.left = Math.random() * 100 + 'vw';
      confetti.style.opacity = '0.8';
      confettiContainer.appendChild(confetti);

      const animation = confetti.animate(
        [
          { transform: 'translateY(0) rotate(0deg)', opacity: 1 },
          {
            transform: `translateY(${window.innerHeight}px) rotate(${
              Math.random() * 360
            }deg)`,
            opacity: 0,
          },
        ],
        {
          duration: 2000 + Math.random() * 3000,
          easing: 'cubic-bezier(0.1, 0.8, 0.2, 1)',
        }
      );

      animation.onfinish = () => {
        confetti.remove();
        if (confettiContainer.children.length === 0) {
          confettiContainer.remove();
        }
      };
    }
  };

  // Cargar categorías y tags
  useEffect(() => {
    const fetchTaxonomies = async () => {
      try {
        const [categoriesRes, tagsRes, authorsRes] = await Promise.all([
          fetch(
            `${process.env.REACT_APP_API_BASE_URL}/categories/with_counts/`
          ),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/tags/`),
          fetch(`${process.env.REACT_APP_API_BASE_URL}/users/`),
        ]);

        if (categoriesRes.ok) {
          const categoriesData = await categoriesRes.json();
          setAllCategories(categoriesData);
        }

        if (tagsRes.ok) {
          const tagsData = await tagsRes.json();
          setAllTags(tagsData);
        }

        if (authorsRes.ok) {
          const authorsData = await authorsRes.json();
          setAllAuthors(authorsData);
        }
      } catch (error) {
        console.error('Error fetching taxonomies:', error);
      }
    };

    fetchTaxonomies();
  }, []);

  // Scroll al final cuando se agregan bloques
  useEffect(() => {
    if (lastActiveBlockId && blockRefs.current[lastActiveBlockId]) {
      blockRefs.current[lastActiveBlockId].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [lastActiveBlockId]);

  // Agregar bloque
  const addBlock = (type) => {
    const newBlock = {
      id: Date.now(),
      type,
      content: '',
      level: 2,
      items: [''],
      ordered: false,
      size: 'large',
      alignment: 'center',
      altText: '',
      caption: '',
      linkUrl: '',
      wpAttributes: {
        aspectRatio: '16/9',
        scale: 'cover',
        sizeSlug: 'large',
        linkDestination: 'none',
      },
    };

    if (type === 'image') {
      newBlock.url = '';
    }

    setBlocks([...blocks, newBlock]);
    setLastActiveBlockId(newBlock.id);
    setShowBlockInserter(false);
  };

  // Actualizar bloque
  const updateBlock = (blockId, updatedData) => {
    setBlocks(
      blocks.map((block) =>
        block.id === blockId ? { ...block, ...updatedData } : block
      )
    );
    setLastActiveBlockId(blockId); // ← Aquí está el cambio
  };

  // Eliminar bloque
  const deleteBlock = (blockId) => {
    setBlocks(blocks.filter((block) => block.id !== blockId));
  };

  const moveBlock = (fromIndex, toIndex) => {
    const newBlocks = [...blocks];
    const [movedBlock] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, movedBlock);
    setBlocks(newBlocks);
  };

  // Manejar imagen destacada
  // const handleFeaturedImageUpload = (imageData) => {
  //   setFeaturedImage(imageData.aws_path || imageData.url);
  // };

  // Crear artículo
  const createArticle = async (status = 'draft') => {
    if (!title.trim()) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'El título es obligatorio',
      });
      return;
    }

    setLoading(true);

    try {
      const articleData = {
        title,
        status,
        featured_image_url: featuredImage,
        comments_enabled: true,
        author_id: selectedAuthors,
        category_ids: selectedCategories,
        tag_ids: selectedTags,
        blocks: blocks.map((block) => ({
          type: block.type,
          content: block.content || '',
          level: block.level,
          items: block.items,
          ordered: block.ordered,
          size: block.size,
          alignment: block.alignment,
          altText: block.altText,
          caption: block.caption,
          linkUrl: block.linkUrl,
          url: block.url,
          wpAttributes: block.wpAttributes,
        })),
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/articles/create-with-blocks`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(articleData),
        }
      );

      if (response.ok) {
        const newArticle = await response.json();

        addNotification({
          type: 'success',
          title: '¡Artículo Creado!',
          message: `Tu artículo ha sido ${
            status === 'published' ? 'publicado' : 'guardado como borrador'
          } exitosamente.`,
          onViewArticle: () => navigate(`/${newArticle.slug}`),
        });

        createConfettiEffect();

        // Redirigir después de un tiempo
        setTimeout(() => {
          navigate(`/${newArticle.slug}`);
        }, 3000);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al crear el artículo');
      }
    } catch (error) {
      console.error('Error creating article:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Error al crear el artículo',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="gutenberg-editor">
      <div className="editor-header">
        <div className="header-left">
          <button onClick={() => navigate(-1)} className="btn-back">
            ← Volver
          </button>
          <h1>Crear Nuevo Artículo</h1>
        </div>
        <div className="header-right">
          <button
            onClick={() => createArticle('draft')}
            disabled={loading}
            className="btn-preview"
          >
            {loading ? '⏳ Guardando...' : '💾 Guardar Borrador'}
          </button>
          <button
            onClick={() => createArticle('published')}
            disabled={loading}
            className="btn-save-primary"
          >
            {loading ? '⏳ Publicando...' : '🚀 Publicar'}
          </button>
        </div>
      </div>

      <div className="editor-container">
        <div className="block-library">
          <h3>Bloques</h3>
          <div className="block-categories">
            <div className="block-category">
              <h4>Texto</h4>
              <button
                onClick={() => addBlock('paragraph')}
                className="block-item"
              >
                📝 Párrafo
              </button>
              <button
                onClick={() => addBlock('heading')}
                className="block-item"
              >
                🏷️ Título
              </button>
            </div>

            <div className="block-category">
              <h4>Medios</h4>
              <button onClick={() => addBlock('image')} className="block-item">
                🖼️ Imagen
              </button>
            </div>

            <div className="block-category">
              <h4>Diseño</h4>
              <button onClick={() => addBlock('list')} className="block-item">
                📋 Lista
              </button>
            </div>
          </div>
        </div>
        <div className="editor-content">
          {/* Título del artículo */}
          <div className="document-settings">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="title-input"
              placeholder="Escribe el título del artículo..."
            />
          </div>
          <div className="blocks-area">
            {blocks.map((block, index) => (
              <div
                key={block.id}
                className="block-wrapper"
                ref={(el) => (blockRefs.current[block.id] = el)}
              >
                <div className="block-controls">
                  <button
                    onClick={() => moveBlock(index, index - 1)}
                    disabled={index === 0}
                    className="btn-move"
                    title="Mover arriba"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => moveBlock(index, index + 1)}
                    disabled={index === blocks.length - 1}
                    className="btn-move"
                    title="Mover abajo"
                  >
                    ↓
                  </button>
                </div>

                {block.type === 'paragraph' && (
                  <EditableBlock
                    block={block}
                    onUpdate={updateBlock}
                    onDelete={() => deleteBlock(block.id)}
                  />
                )}
                {block.type === 'heading' && (
                  <HeadingBlock
                    block={block}
                    onUpdate={updateBlock}
                    onDelete={() => deleteBlock(block.id)}
                  />
                )}
                {block.type === 'image' && (
                  <ImageBlock
                    block={block}
                    onUpdate={updateBlock}
                    onDelete={() => deleteBlock(block.id)}
                  />
                )}
                {block.type === 'list' && (
                  <EditableBlock
                    block={block}
                    onUpdate={updateBlock}
                    onDelete={() => deleteBlock(block.id)}
                  />
                )}
              </div>
            ))}

            {/* Insertor de bloques flotante */}
            <div className="block-inserter">
              <button
                onClick={() => setShowBlockInserter(!showBlockInserter)}
                className="inserter-toggle"
              >
                + Añadir bloque
              </button>

              {showBlockInserter && (
                <div className="inserter-popover">
                  <div className="inserter-search">
                    <input type="text" placeholder="Buscar bloques..." />
                  </div>
                  <div className="inserter-blocks">
                    <button onClick={() => addBlock('paragraph')}>
                      📝 Párrafo
                    </button>
                    <button onClick={() => addBlock('heading')}>
                      🏷️ Título
                    </button>
                    <button onClick={() => addBlock('image')}>🖼️ Imagen</button>
                    <button onClick={() => addBlock('list')}>📋 Lista</button>
                  </div>
                </div>
              )}
            </div>

            <div ref={blockRefs} />
          </div>
        </div>

        {/* Panel de configuración */}
        <div className="editor-sidebar">
          <div className="sidebar-panel">
            <h3>Configuración del artículo</h3>

            {/* Gestor de Categorías y Tags */}
            <div className="sidebar-section">
              <h4>Categorías y Etiquetas</h4>
              <TaxonomyManager
                selectedCategories={selectedCategories}
                setSelectedCategories={setSelectedCategories}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                selectedAuthor={selectedAuthors}
                setSelectedAuthor={setSelectedAuthors}
                allCategories={allCategories}
                allTags={allTags}
                allAuthors={allAuthors}
              />
            </div>

            {/* Imagen destacada */}
            <div className="sidebar-section">
              <h4>Imagen destacada</h4>
              <ImageUploader
                onImageUploaded={(image) => setFeaturedImage(image.url)}
              />
              {featuredImage && (
                <div className="featured-image-preview">
                  <img src={featuredImage} alt="Imagen destacada" />
                  <button
                    onClick={() => setFeaturedImage('')}
                    className="btn-remove"
                  >
                    ❌
                  </button>
                </div>
              )}
            </div>
            <div className="sidebar-section">
              <h4>Subir nueva imagen</h4>
              <ImageUploader
                onImageUploaded={(imageData) => {
                  addBlock('image', {
                    url: imageData.url,
                    aws_path: imageData.aws_path,
                    altText: imageData.alt_text || '',
                    size: 'large',
                    alignment: 'center',
                    wpAttributes: {
                      aspectRatio: imageData.aspectRatio || '16/9',
                      scale: 'cover',
                      ...(imageData.dimensions && {
                        width: imageData.dimensions.width,
                        height: imageData.dimensions.height,
                      }),
                    },
                    linkUrl: imageData.aws_path || imageData.url,
                  });
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <NotificationContainer
        notifications={notifications}
        removeNotification={removeNotification}
      />
    </div>
  );
}
